'use client';

import { c as _c } from "react/compiler-runtime";
import type { CreditRangeEnum } from '@cc-shopping/contracts/credit-cards-page/roundup/utils';
import { usePageAction } from '@cc-shopping/platform-analytics/hooks/usePageAction';
import type { MobileFilterCloseReason } from 'src/features/roundup-filters/types';
import { useShallow } from 'zustand/shallow';
import { useOffersFilterStore } from '../../Context';
import { type FilterOffersByType, FilterType, type SortOffersByType } from '../../enums';
import type { OffersFilterStoreState } from '../../types';
import { getLabelForFilterItem } from '../../utils';
export function useFiltersAnalytics() {
  const $ = _c(27);
  const {
    preComputedFilterOfferIds: t0
  } = useOffersFilterStore(useShallow(_temp));
  const {
    issuersIdNameMap
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      actionSection: "roundup_filters"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const firePageAction = usePageAction(t1);
  let t2;
  if ($[1] !== firePageAction || $[2] !== issuersIdNameMap) {
    t2 = value => {
      const label = getLabelForFilterItem({
        type: FilterType.CARD_FEATURE,
        item: value,
        issuersIdNameMap
      });
      firePageAction({
        action_name: "card_feature_filter",
        action_input: label,
        action_type: "select"
      });
    };
    $[1] = firePageAction;
    $[2] = issuersIdNameMap;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const fireCardFeatureUpdateEvents = t2;
  let t3;
  if ($[4] !== firePageAction || $[5] !== issuersIdNameMap) {
    t3 = value_0 => {
      const label_0 = getLabelForFilterItem({
        type: FilterType.SORT_BY,
        item: value_0,
        issuersIdNameMap
      });
      firePageAction({
        action_name: "sorting_filter",
        action_input: label_0,
        action_type: "select"
      });
    };
    $[4] = firePageAction;
    $[5] = issuersIdNameMap;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const fireSortByUpdateEvents = t3;
  let t4;
  if ($[7] !== firePageAction || $[8] !== issuersIdNameMap) {
    t4 = t5 => {
      const {
        newSelectedValues,
        prevSelectedValues
      } = t5;
      const addedValue = newSelectedValues.find(value_1 => !prevSelectedValues.includes(value_1));
      const removedValue = prevSelectedValues.find(prevValue => !newSelectedValues.includes(prevValue));
      if (addedValue) {
        const label_1 = getLabelForFilterItem({
          type: FilterType.ISSUER,
          item: addedValue,
          issuersIdNameMap
        });
        firePageAction({
          action_name: "issuers_filter",
          action_input: label_1,
          action_type: "select"
        });
      } else {
        if (removedValue) {
          const label_2 = getLabelForFilterItem({
            type: FilterType.ISSUER,
            item: removedValue,
            issuersIdNameMap
          });
          firePageAction({
            action_name: "issuers_filter",
            action_input: label_2,
            action_type: "deselect"
          });
        }
      }
    };
    $[7] = firePageAction;
    $[8] = issuersIdNameMap;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const fireIssuerUpdateEvents = t4;
  let t5;
  if ($[10] !== firePageAction || $[11] !== issuersIdNameMap) {
    t5 = t6 => {
      const {
        newSelectedValues: newSelectedValues_0,
        prevSelectedValues: prevSelectedValues_0
      } = t6;
      const addedValue_0 = newSelectedValues_0.find(selected => !prevSelectedValues_0.includes(selected));
      const removedValue_0 = prevSelectedValues_0.find(prevSelected => !newSelectedValues_0.includes(prevSelected));
      if (addedValue_0) {
        const label_3 = getLabelForFilterItem({
          type: FilterType.CREDIT_RANGE,
          item: addedValue_0,
          issuersIdNameMap
        });
        firePageAction({
          action_name: "credit_range_filter",
          action_input: label_3,
          action_type: "select"
        });
      } else {
        if (removedValue_0) {
          const label_4 = getLabelForFilterItem({
            type: FilterType.CREDIT_RANGE,
            item: removedValue_0,
            issuersIdNameMap
          });
          firePageAction({
            action_name: "credit_range_filter",
            action_input: label_4,
            action_type: "deselect"
          });
        }
      }
    };
    $[10] = firePageAction;
    $[11] = issuersIdNameMap;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const fireCreditRangeUpdateEvents = t5;
  let t6;
  if ($[13] !== firePageAction) {
    t6 = label_5 => {
      firePageAction({
        action_name: "clear_filter",
        action_input: label_5,
        action_type: "select"
      });
    };
    $[13] = firePageAction;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const firePillsRemovalEvents = t6;
  let t7;
  if ($[15] !== firePageAction) {
    t7 = () => {
      firePageAction({
        action_name: "clear_filter",
        action_input: "Clear all",
        action_type: "select"
      });
    };
    $[15] = firePageAction;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const fireClearAllEvents = t7;
  let t8;
  if ($[17] !== firePageAction) {
    t8 = t9 => {
      const {
        reason
      } = t9;
      let actionName;
      bb28: switch (reason) {
        case "apply":
          {
            actionName = "apply_cta";
            break bb28;
          }
        case "close":
          {
            actionName = "close_x";
            break bb28;
          }
        case "esc_key":
          {
            actionName = "esc_key";
          }
      }
      firePageAction({
        action_name: actionName,
        action_input: null,
        action_type: "select"
      });
    };
    $[17] = firePageAction;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const fireApplyFilterEvents = t8;
  let t9;
  if ($[19] !== fireApplyFilterEvents || $[20] !== fireCardFeatureUpdateEvents || $[21] !== fireClearAllEvents || $[22] !== fireCreditRangeUpdateEvents || $[23] !== fireIssuerUpdateEvents || $[24] !== firePillsRemovalEvents || $[25] !== fireSortByUpdateEvents) {
    t9 = {
      fireCardFeatureUpdateEvents,
      fireCreditRangeUpdateEvents,
      fireIssuerUpdateEvents,
      fireSortByUpdateEvents,
      firePillsRemovalEvents,
      fireClearAllEvents,
      fireApplyFilterEvents
    };
    $[19] = fireApplyFilterEvents;
    $[20] = fireCardFeatureUpdateEvents;
    $[21] = fireClearAllEvents;
    $[22] = fireCreditRangeUpdateEvents;
    $[23] = fireIssuerUpdateEvents;
    $[24] = firePillsRemovalEvents;
    $[25] = fireSortByUpdateEvents;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}
function _temp(state) {
  return {
    preComputedFilterOfferIds: state.preComputedFilterOfferIds
  };
}